import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import axios from "axios";
import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";
import ConstList from "../utils/ConstList";

type Customer = {
  company_id: number;
  company_name: string;
};

type Project = {
  project_id: number;
  project_name: string;
  project_summary: string;
  status: string;
  report_count: number;
};

type Report = {
  sales_record_id: number;
  sales_date?: string;
  contact_person?: string;
  sales_person?: string;
  sales_status?: string;
  proposal_details?: string;
  customer_needs?: string;
  next_action?: string;
  next_schedule?: string;
};

type Props = {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
};

const text = (value: unknown) => String(value ?? "").trim();

export default function CustomerProjectDetailDialog({
  open,
  customer,
  onClose,
}: Props) {
  const loginUserData = useRecoilValue(LoginUserAtom);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;

    const loadProjects = async () => {
      setLoading(true);
      setSelectedProject(null);
      setReports([]);
      try {
        const response = await axios.post(
          `${ConstList.getApiUrl()}ProjectMaster.php`,
          {
            flag: "Project_List",
            access_token: loginUserData.accessToken,
            algorithm: loginUserData.algorithm,
          },
          ConstList.AXIOS_CONFIG,
        );
        const rawProjects = response.data?.data?.projects ?? [];
        setProjects(
          Array.isArray(rawProjects)
            ? rawProjects
                .filter((item: any) => Number(item.company_id) === customer.company_id)
                .map((item: any) => ({
                  project_id: Number(item.id ?? item.project_id),
                  project_name: text(item.name ?? item.project_name) || "名称未設定案件",
                  project_summary: text(item.project_summary),
                  status: text(item.status),
                  report_count: Number(item.report_count ?? 0),
                }))
                .filter((item: Project) => item.project_id > 0)
            : [],
        );
      } finally {
        setLoading(false);
      }
    };

    void loadProjects();
  }, [customer, loginUserData.accessToken, loginUserData.algorithm, open]);

  const loadReports = async (project: Project) => {
    setSelectedProject(project);
    setReports([]);
    setReportLoading(true);
    try {
      const response = await axios.post(
        `${ConstList.getApiUrl()}ProjectMaster.php`,
        {
          flag: "Project_Report_List",
          project_id: project.project_id,
          access_token: loginUserData.accessToken,
          algorithm: loginUserData.algorithm,
        },
        ConstList.AXIOS_CONFIG,
      );
      const rawReports = response.data?.data?.reports ?? [];
      setReports(Array.isArray(rawReports) ? rawReports : []);
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#374151",
          color: "#FFFFFF",
          borderBottom: "1px solid rgba(255,255,255,0.16)",
        }}
      >
        <BusinessOutlinedIcon />
        <Typography component="span" fontWeight={800}>
          {customer?.company_name || "企業"} 詳細
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "background.default" }}>
        <Card elevation={0} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography fontWeight={800} sx={{ mb: 1.5 }}>
              案件一覧
            </Typography>
            {loading ? (
              <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={26} />
              </Box>
            ) : projects.length === 0 ? (
              <Typography color="text.secondary">
                この企業に紐づく案件はありません。
              </Typography>
            ) : (
              <Stack spacing={1}>
                {projects.map((project) => (
                  <React.Fragment key={project.project_id}>
                    <Box
                      onDoubleClick={() => void loadReports(project)}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        cursor: "pointer",
                        backgroundColor:
                          selectedProject?.project_id === project.project_id
                            ? "rgba(55,65,81,0.08)"
                            : "background.paper",
                        "&:hover": { backgroundColor: "grey.100" },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography fontWeight={800}>{project.project_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          履歴 {project.report_count}件
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {project.project_summary || "案件概要未登録"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ダブルクリックで営業履歴を表示
                      </Typography>
                    </Box>

                    {selectedProject?.project_id === project.project_id && (
                      <Box sx={{ ml: { xs: 0, md: 3 }, mt: -0.25, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                          <Typography fontWeight={800}>{project.project_name}の営業履歴</Typography>
                          <Chip icon={<HistoryOutlinedIcon />} label={`${reports.length}件`} size="small" />
                        </Stack>
                        {reportLoading ? (
                          <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : reports.length === 0 ? (
                          <Typography color="text.secondary" align="center" sx={{ py: 1 }}>
                            取引履歴がありません。
                          </Typography>
                        ) : (
                          <Stack spacing={1.25}>
                            {reports.map((report) => (
                              <Box key={report.sales_record_id} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1.5, backgroundColor: "background.paper" }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                  <Typography fontWeight={800}>{report.sales_date || "日付未入力"}</Typography>
                                  <Typography variant="body2" color="text.secondary">{report.sales_person || "担当者未設定"}</Typography>
                                </Stack>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2">顧客窓口：{report.contact_person || "-"}</Typography>
                                <Typography variant="body2">進捗状況：{report.sales_status || "-"}</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>提案内容：{report.proposal_details || report.customer_needs || "-"}</Typography>
                                <Typography variant="body2">次回アクション：{report.next_action || report.next_schedule || "-"}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
